import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from "@mui/material";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import GroupsIcon from "@mui/icons-material/Groups";
import PublicIcon from "@mui/icons-material/Public";
import { Tables } from "../../../database.types";
import { useLeagueOverview } from "./useLeagueOverview";
import { LeagueHeader } from "./LeagueHeader";
import { TeamsTabContent } from "./TeamsTabContent";
import { GamesTabContent } from "./GamesTabContent";
import { ConfirmationDialogs } from "./ConfirmationDialogs";

interface LeagueOverviewProps {
  league: Tables<"leagues">;
  onBackToLeagueSelection?: () => void;
  onLeagueUpdate?: (league: Tables<"leagues">) => void;
}

const LeagueOverview: React.FC<LeagueOverviewProps> = ({
  league,
  onBackToLeagueSelection,
  onLeagueUpdate,
}) => {
  const theme = useTheme();
  const hook = useLeagueOverview({ league, onLeagueUpdate });

  // Handle loading state
  if (hook.loading) {
    return (
      <Box
        height="100vh"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={theme.palette.primary.dark}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Handle error state
  if (hook.error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2, mb: 2 }} variant="filled">
          {hook.error.message}
        </Alert>
      </Container>
    );
  }

  // Handle no league ID
  if (!hook.user || !league) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" variant="filled">
          No league information available. Please check your account settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        maxHeight: "calc(100vh - 32px)",
        height: "calc(100vh - 32px)",
        bgcolor: theme.palette.primary.dark,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow="hidden"
        sx={{ py: { xs: 2, sm: 3 } }}
      >
        <LeagueHeader
          league={league}
          isMobile={hook.isMobile}
          isEditingName={hook.isEditingName}
          editedName={hook.editedName}
          setEditedName={hook.setEditedName}
          setIsEditingName={hook.setIsEditingName}
          onBackToLeagueSelection={onBackToLeagueSelection}
          onSaveLeagueName={hook.onSaveLeagueName}
          onCopyJoinCode={hook.onCopyJoinCode}
          onSignOutClick={() => hook.setConfirmSignOut(true)}
        />

        {/* Tab Navigation */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            p: 1,
            transition: "box-shadow 0.3s ease-in-out",
            backgroundColor: theme.palette.primary.main,
            overflow: "hidden",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tabs
            value={hook.activeTab}
            onChange={(_, newValue) => hook.setActiveTab(newValue)}
            slotProps={{
              indicator: {
                style: {
                  height: "100%",
                  top: 0,
                  borderRadius: 8,
                  backgroundColor: theme.palette.primary.dark,
                  zIndex: 0,
                  transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                },
              },
            }}
            centered
            sx={{
              px: { xs: 4, sm: 8 },
              "& .MuiTabs-flexContainer": {
                justifyContent: "center",
              },
              "& .MuiTab-root": {
                position: "relative",
                zIndex: 1,
                fontWeight: "bold",
                width: "33.33%",
                py: 2,
                borderRadius: 2,
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.dark, 0.3),
                },
                color: "white",
              },
            }}
          >
            <Tab
              icon={<GroupsIcon />}
              iconPosition="start"
              label="Teams"
              value="teams"
              focusRipple
            />
            <Tab
              icon={<SportsEsportsIcon />}
              iconPosition="start"
              label="My Games"
              value="my-games"
              focusRipple
            />
            <Tab
              icon={<PublicIcon />}
              iconPosition="start"
              label="League Games"
              value="league-games"
              focusRipple
            />
          </Tabs>

          {hook.activeTab === "teams" && (
            <TeamsTabContent
              teams={hook.teams}
              isOwner={hook.isOwner}
              currentUserMembership={hook.currentUserMembership}
              leagueMembers={hook.leagueMembers}
              unassignedMembers={hook.unassignedMembers}
              user={hook.user}
              newTeam={hook.newTeam}
              setNewTeam={hook.setNewTeam}
              onCreateNewTeam={hook.onCreateNewTeam}
              onDeleteTeam={hook.onDeleteTeam}
              onSaveTeam={hook.onSaveTeam}
              refreshMemberData={hook.refreshMemberData}
            />
          )}

          {hook.activeTab === "my-games" && (
            <GamesTabContent
              variant="my-games"
              games={hook.myGames}
              teams={hook.teams}
              teamsMap={hook.teamsMap}
              onCopyGameLink={hook.onCopyGameLink}
              onDeleteGame={hook.onDeleteGame}
              onStartGame={(game) => hook.setGameToStart(game)}
              newGameName={hook.newGameName}
              setNewGameName={hook.setNewGameName}
              onCreateNewGame={hook.onCreateNewGame}
            />
          )}

          {hook.activeTab === "league-games" && (
            <GamesTabContent
              variant="league-games"
              games={hook.leagueGames}
              teams={hook.teams}
              teamsMap={hook.teamsMap}
              onCopyGameLink={hook.onCopyGameLink}
              onDeleteGame={hook.onDeleteGame}
              onStartGame={(game) => hook.setGameToStart(game)}
            />
          )}
        </Paper>
      </Box>

      <ConfirmationDialogs
        gameToStart={hook.gameToStart}
        confirmSignOut={hook.confirmSignOut}
        onConfirmStart={hook.onConfirmStart}
        onCancelStart={() => hook.setGameToStart(null)}
        onConfirmSignOut={async () => {
          await hook.signOut();
          hook.nav("/auth");
        }}
        onCancelSignOut={() => hook.setConfirmSignOut(false)}
      />
    </Box>
  );
};

export default LeagueOverview;
